import mapboxgl from 'mapbox-gl';

/**
 * Performs reverse geocoding using Mapbox API and returns structured address data.
 * 
 * @param {number} longitude - The longitude of the location.
 * @param {number} latitude - The latitude of the location.
 * @returns {Promise<Object>} A promise that resolves to the structured address object:
 *                            { street, city, stateShortCode, preferredAddress, coordinates }
 * @throws Will throw an error if the API call fails or if no valid features are found.
 */
export const reverseGeocode = async (longitude, latitude) => {
    if (!longitude || !latitude) {
        throw new Error('Longitude and latitude are required.');
    }

    try {
        const response = await fetch(
            `https://api.mapbox.com/search/geocode/v6/reverse?longitude=${longitude}&latitude=${latitude}&access_token=${mapboxgl.accessToken}`
        );
        const data = await response.json();

        if (data.features && data.features.length > 0) {
            const feature = data.features[0]; // Use the first feature
            const { properties, geometry } = feature;
            const context = properties?.context;

            // Extract components
            const street = context?.address?.name || properties?.name || '';
            const city = context?.place?.name || '';
            const stateShortCode = context?.region?.region_code || '';

            // Build the preferred address
            const preferredAddress = [street, city, stateShortCode].filter((part) => part).join(', ');

            return {
                street,
                city,
                stateShortCode,
                preferredAddress,
                coordinates: geometry.coordinates,
            };
        } else {
            throw new Error('No valid features found in the response.');
        }
    } catch (error) {
        console.error('Error during reverse geocoding:', error);
        throw new Error('Failed to perform reverse geocoding.');
    }
};